import apiClient from "@shared/config/axiosConfig";
import fileService from "./filesService";

const getAllUsers = async () => {
  try {
    const response = await apiClient.get("/users");
    return response.data;
  } catch (error) {
    console.error("Error al obtener los usuarios:", error);
    throw error;
  }
};

const getUserById = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error al obtener el usuario:", error);
    throw error;
  }
};

const createUser = async (user) => {
  try {
    const response = await apiClient.post("/users", user);
    return response.data;
  } catch (error) {
    console.error("Error al crear el usuario:", error);
    throw error;
  }
};

const updateUser = async (userId, user) => {
  try {
    const response = await apiClient.put(`/users/${userId}`, user);
    return response.data;
  } catch (error) {
    console.error("Error al actualizar el usuario:", error);
    throw error;
  }
};

const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar el usuario:", error);
    throw error;
  }
};

const deleteUserPermanent = async (userId) => {
  try {
    const response = await apiClient.delete(`/users/${userId}/permanent`);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar permanentemente el usuario:", error);
    throw error;
  }
};

const cargarDocumentos = async (firmaPath, identificacionPath, userId) => {
  try {
    // Validaciones previas
    if (!firmaPath || !identificacionPath || !userId) {
      throw new Error(
        "Todos los parámetros son requeridos: firmaPath, identificacionPath, userId",
      );
    }

    console.log("📋 Enviando documentos al servidor:", {
      userId,
      firmaPath: firmaPath.substring(0, 50) + "...",
      identificacionPath: identificacionPath.substring(0, 50) + "...",
    });

    // Configuración con timeout extendido para Railway (incluye múltiples intentos SMTP)
    const config = {
      timeout: 180000, // 3 minutos para permitir múltiples configuraciones de fallback
      headers: {
        "Content-Type": "application/json",
      },
    };

    const payload = {
      firmaPath,
      identificacionPath,
    };

    const response = await apiClient.post(
      `/users/${userId}/cargar-firma-identificacion`,
      payload,
      config,
    );

    console.log("✅ Respuesta del servidor recibida:", {
      status: response.status,
      data: response.data,
      emailSent: response.data.emailSent,
      emailError: response.data.emailError,
      message: response.data.message,
      success: response.data.success,
    });

    // Mostrar información específica sobre el email
    if (response.data.emailSent === false) {
      console.warn(
        "âš ï¸ ADVERTENCIA: Documentos guardados pero falló el envío de email:",
        response.data.emailError,
      );
    } else if (response.data.emailSent === true) {
      console.log("ðŸ“§ Email enviado exitosamente a los administradores");
    }

    return response.data;
  } catch (error) {
    console.error("âŒ Error al cargar los documentos del usuario:", error);

    // Manejo mejorado de errores
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      throw new Error(
        "El servidor tardó demasiado en procesar los documentos. La operación puede haberse completado, por favor contacta al administrador para verificar.",
      );
    } else if (error.response?.status === 400) {
      throw new Error(
        `Error de validación: ${
          error.response.data?.message || "Datos inválidos"
        }`,
      );
    } else if (error.response?.status === 401) {
      throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
    } else if (error.response?.status === 403) {
      throw new Error("No tienes permisos para realizar esta operación.");
    } else if (error.response?.status >= 500) {
      throw new Error(
        "Error interno del servidor. Por favor, intenta de nuevo más tarde.",
      );
    } else if (error.request) {
      throw new Error(
        "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
      );
    } else {
      throw new Error(`Error inesperado: ${error.message}`);
    }
  }
};

const validateUserDocuments = async (userId, tempToken) => {
  try {
    console.log("ðŸš€ validateUserDocuments - Iniciando validación con:", {
      userId,
      tempToken: tempToken ? `${tempToken.substring(0, 20)}...` : null,
      url: `/users/validate-user`,
    });

    // IMPORTANTE: No enviar el token de autorización normal, solo el token temporal
    // Esto previene conflictos con el middleware de autenticación
    const response = await apiClient.post(
      `/users/validate-user`,
      {
        idusuario: userId,
        token: tempToken,
      },
      {
        headers: {
          // Sobrescribir el header Authorization para que NO use el token normal
          Authorization: "",
        },
      },
    );

    console.log("✅ validateUserDocuments - Respuesta exitosa:", response.data);
    return response.data;
  } catch (error) {
    console.error("âŒ validateUserDocuments - Error al validar:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      userId,
      tempToken: tempToken ? `${tempToken.substring(0, 20)}...` : null,
    });
    throw error;
  }
};

const rejectUserDocuments = async (userId, tempToken) => {
  try {
    const response = await apiClient.post(
      `/users/reject-user`,
      {
        idusuario: userId,
        token: tempToken,
      },
      {
        headers: {
          Authorization: "",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al rechazar documentos del usuario:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

const updateUserProfile = async (userId, user, file) => {
  try {
    // Si hay un archivo, actualiza la imagen de perfil usando la nueva ruta específica
    if (file) {
      // Primero subimos el archivo
      const fileResponse = await fileService.uploadFile(file, "ImagesProfiles");

      if (fileResponse && fileResponse.key) {
        // Actualiza la imagen de perfil usando la ruta específica
        await apiClient.put(`/users/${userId}/profile-image`, {
          imageProfile: fileResponse.key,
        });

        // Luego actualiza los datos del usuario (sin imagen)
        const updateResponse = await apiClient.put(`/users/${userId}`, user);

        return updateResponse;
      } else {
        throw new Error(
          "No se recibió una respuesta válida al subir el archivo",
        );
      }
    } else {
      // Si no hay archivo, solo actualiza los datos del usuario
      const updateResponse = await apiClient.put(`/users/${userId}`, user);
      return updateResponse;
    }
  } catch (error) {
    console.error("Error al actualizar el perfil del usuario:", error);
    if (error.response) {
      console.error("Datos del error:", error.response.data);
      console.error("Estado del error:", error.response.status);
    }
    throw error;
  }
};

const updateProfileImage = async (userId, imageProfile) => {
  try {
    const response = await apiClient.put(`/users/${userId}/profile-image`, {
      imageProfile,
    });
    return response.data;
  } catch (error) {
    console.error("Error al actualizar la imagen de perfil:", error);
    throw error;
  }
};

const deleteImageProfile = async (userId) => {
  try {
    const response = await apiClient.delete(`/users/${userId}/profile-image`);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar la imagen de perfil:", error);
    throw error;
  }
};

const cambiarRolUser = async (userId, rol) => {
  try {
    const response = await apiClient.put(`/users/${userId}`, {
      idrol: rol,
    });
    return response.data;
  } catch (error) {
    console.error("Error al cambiar el rol del usuario:", error);
    throw error;
  }
};

const actualizarRol = async (payload) => {
  try {
    const response = await apiClient.put(
      `/users/actualizarRol/${payload.idusuario}`,
      {
        idrol: payload.idrol,
        idplanta: payload.idplanta,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al actualizar el rol del usuario:", error);
    throw error;
  }
};

const getDepartamentosUsuario = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}/departamentos`);
    return response.data;
  } catch (error) {
    console.error("Error al obtener departamentos del usuario:", error);
    throw error;
  }
};

const cargarSoloFirma = async (firmaPath, userId) => {
  try {
    // Validaciones previas
    if (!firmaPath || !userId) {
      throw new Error("Todos los parámetros son requeridos: firmaPath, userId");
    }

    console.log("ðŸ“‹ Cargando solo firma al servidor:", {
      userId,
      firmaPath: firmaPath.substring(0, 50) + "...",
    });

    const config = {
      timeout: 60000, // 1 minuto para esta operación
      headers: {
        "Content-Type": "application/json",
      },
    };

    const payload = {
      firmaPath,
    };

    const response = await apiClient.post(
      `/users/${userId}/cargar-firma`,
      payload,
      config,
    );

    console.log("✅ Firma cargada exitosamente:", {
      status: response.status,
      message: response.data.message,
    });

    return response.data;
  } catch (error) {
    console.error("âŒ Error al cargar la firma del usuario:", error);

    // Si el endpoint específico no existe, usar el genérico con placeholder
    if (error.response?.status === 404) {
      console.log(
        "ðŸ“‹ Endpoint específico no encontrado, usando cargar-firma-identificacion con placeholder",
      );
      return await cargarDocumentos(
        firmaPath,
        "admin-upload/no-identification.png",
        userId,
      );
    }

    // Manejo mejorado de errores
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      throw new Error("El servidor tardó demasiado en procesar la firma.");
    } else if (error.response?.status === 400) {
      throw new Error(
        `Error de validación: ${
          error.response.data?.message || "Datos inválidos"
        }`,
      );
    } else if (error.response?.status === 401) {
      throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
    } else if (error.response?.status === 403) {
      throw new Error("No tienes permisos para realizar esta operación.");
    } else if (error.response?.status >= 500) {
      throw new Error(
        "Error interno del servidor. Por favor, intenta de nuevo más tarde.",
      );
    } else if (error.request) {
      throw new Error(
        "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
      );
    } else {
      throw new Error(`Error inesperado: ${error.message}`);
    }
  }
};

const adminValidateUser = async (userId) => {
  try {
    const response = await apiClient.put(`/users/${userId}/admin-validate`);
    return response.data;
  } catch (error) {
    console.error("Error al validar usuario desde admin:", error);
    throw error;
  }
};

const adminRejectUser = async (userId) => {
  try {
    const response = await apiClient.put(`/users/${userId}/admin-reject`);
    return response.data;
  } catch (error) {
    console.error("Error al rechazar firma desde admin:", error);
    throw error;
  }
};

const resetPasswordByDev = async (userId) => {
  try {
    const response = await apiClient.post(`/users/${userId}/dev-reset-password`);
    return response.data;
  } catch (error) {
    console.error("Error al restablecer contraseña por dev:", error);
    throw error;
  }
};

const createUserWithTempPassword = async (userData) => {
  try {
    const response = await apiClient.post("/users/create-with-temp-password", {
      nombreusuario: userData.nombreusuario,
      correoelectronico: userData.correoelectronico,
      idrol: parseInt(userData.idrol, 10),
      idplanta: userData.idplanta ? parseInt(userData.idplanta, 10) : null,
    });

    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      const backendErrors = error.response.data?.errors;
      const firstValidationMessage = Array.isArray(backendErrors)
        ? backendErrors[0]?.msg
        : null;
      const message =
        firstValidationMessage ||
        error.response.data?.message ||
        "Error de validación";
      if (message.includes("ya existe")) {
        throw new Error("El correo electrónico ya está registrado");
      }
      throw new Error(message);
    } else if (error.response?.status === 401) {
      throw new Error("No autenticado. Por favor, inicia sesión nuevamente.");
    } else if (error.response?.status === 403) {
      throw new Error("No tienes permisos para crear usuarios");
    } else if (error.response?.status >= 500) {
      throw new Error("Error interno del servidor");
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor");
    }

    throw new Error(error.message || "Error al crear el usuario");
  }
};

const userService = {
  getAllUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  deleteUserPermanent,
  cargarDocumentos,
  cargarSoloFirma,
  validateUserDocuments,
  rejectUserDocuments,
  adminValidateUser,
  adminRejectUser,
  resetPasswordByDev,
  createUserWithTempPassword,
  updateUserProfile,
  updateProfileImage,
  deleteImageProfile,
  cambiarRolUser,
  actualizarRol,
  getDepartamentosUsuario,
};

export default userService;
